// UsersTable.js - Main Users Table Component
import React, { useState, useEffect } from "react";
import {
  Table,
  Button,
  Input,
  Select,
  Row,
  Col,
  Tag,
  Space,
  Tooltip,
  Card,
  message,
  Modal,
} from "antd";
import {
  Search,
  Filter,
  Eye,
  FileText,
  UserCheck,
  UserX,
  RefreshCw,
  Download,
} from "lucide-react";
import { API_METHODS } from "../../../constants";
import useApi from "../../../hooks/useApi";

const { Option } = Select;

function UsersTable({ onViewDetails, onViewOrderHistory, refreshTrigger }) {
  const [filters, setFilters] = useState({
    pageNumber: 1,
    status: "all",
    verification_status: "all",
  });
  const [messageApi, contextHolder] = message.useMessage();
  const [modal, modalContextHolder] = Modal.useModal();

  // API hooks
  const [usersResponse, getUsersRequest] = useApi();
  const [toggleResponse, toggleUserRequest] = useApi();

  // Fetch users
  const fetchUsers = () => {
    const queryParams = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value && value !== "all" && value !== "") {
        queryParams.append(key, value);
      }
    });
    getUsersRequest(
      null,
      API_METHODS.GET,
      `/admin/users/all?${queryParams.toString()}`
    );
  };

  useEffect(() => {
    fetchUsers();
  }, [filters, refreshTrigger]);

  // Handle toggle user status
  const handleToggleStatus = (userId, currentStatus, userName) => {
    const action = currentStatus ? "disable" : "enable";
    modal.confirm({
      title: `${action.charAt(0).toUpperCase() + action.slice(1)} User`,
      content: `Are you sure you want to ${action} user: ${userName}?`,
      okText: "Yes",
      cancelText: "Cancel",
      okButtonProps: {
        style: action === "disable"
          ? { backgroundColor: "#ff4d4f", borderColor: "#ff4d4f" }
          : { backgroundColor: "#52c41a", borderColor: "#52c41a" },
      },
      onOk: () => {
        toggleUserRequest(
          { action },
          API_METHODS.PUT,
          `/admin/users/status/${userId}`
        );
      },
    });
  };

  // Handle toggle response
  useEffect(() => {
    if (toggleResponse.data && !toggleResponse.isLoading) {
      messageApi.success("User status updated successfully!");
      fetchUsers(); // Refresh the table
    } else if (toggleResponse.error) {
      messageApi.error(toggleResponse.error || "Failed to update user status");
    }
  }, [toggleResponse]);

  // Handle filter changes
  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value, pageNumber: 1 }));
  };

  // Handle pagination
  const handlePageChange = (page) => {
    setFilters((prev) => ({ ...prev, pageNumber: page }));
  };

  // Table columns
  const columns = [
    {
      title: "User Details",
      key: "user_details",
      render: (record) => (
        <div>
          <div className="font-semibold text-gray-800">
            {record.first_name} {record.last_name}
          </div>
          {record.phone && (
            <div className="text-sm text-gray-500">{record.phone}</div>
          )}
        </div>
      ),
    },
    {
      title: "Location",
      key: "location",
      render: (record) => (
        <div>
          {record.city && (
            <div className="text-sm text-gray-600">{record.city}</div>
          )}
          {record.state && (
            <div className="text-sm text-gray-500">{record.state}</div>
          )}
          {!record.city && !record.state && (
            <span className="text-gray-400">Not provided</span>
          )}
        </div>
      ),
    },
    {
      title: "Status",
      key: "status",
      render: (record) => (
        <div>
          <Tag color={record.is_verified ? "green" : "orange"}>
            {record.is_verified ? "Verified" : "Unverified"}
          </Tag>
          <div className="mt-1">
            <Tag color="blue">Active</Tag>
          </div>
        </div>
      ),
    },
    {
      title: "Orders",
      dataIndex: "total_orders",
      key: "total_orders",
      align: "center",
      render: (orders) => (
        <div className="text-center">
          <div className="text-lg font-bold text-orange-600">{orders}</div>
          <div className="text-xs text-gray-500">orders</div>
        </div>
      ),
    },
    {
      title: "Joined",
      dataIndex: "created_on",
      key: "created_on",
      render: (date) => (
        <div className="text-sm">
          {new Date(date).toLocaleDateString("en-IN")}
        </div>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (record) => (
        <Space size="small">
          <Tooltip title="View Details">
            <Button
              size="small"
              icon={<Eye className="h-4 w-4" />}
              onClick={() => onViewDetails(record.id)}
              style={{ backgroundColor: "#f97316", borderColor: "#f97316", color: "#fff" }}
            />
          </Tooltip>

          <Tooltip title="Order History">
            <Button
              size="small"
              icon={<FileText className="h-4 w-4" />}
              onClick={() => onViewOrderHistory(record.id)}
              disabled={record.total_orders === 0}
              style={{ backgroundColor: "#1677ff", borderColor: "#1677ff", color: "#fff" }}
            />
          </Tooltip>

          <Tooltip title="Enable/Disable User">
            <Button
              size="small"
              icon={<UserCheck className="h-4 w-4" />}
              onClick={() =>
                handleToggleStatus(
                  record.id,
                  true, // assuming active status
                  [record.first_name, record.last_name].filter(Boolean).join(" ")
                )
              }
              loading={toggleResponse.isLoading}
              style={{ backgroundColor: "#52c41a", borderColor: "#52c41a", color: "#fff" }}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  const users = usersResponse?.data || [];
  const pagination = usersResponse?.pagination || {};

  return (
    <>
      {contextHolder}
      {modalContextHolder}
      <div>
        {/* Filters */}
        <Card size="small" className="mb-4">
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} md={8}>
              <Select
                placeholder="Filter by Status"
                value={filters.status}
                onChange={(value) => handleFilterChange("status", value)}
                style={{ width: "100%" }}
              >
                <Option value="all">All Status</Option>
                <Option value="active">Active Users</Option>
                <Option value="disabled">Disabled Users</Option>
              </Select>
            </Col>

            <Col xs={24} sm={12} md={8}>
              <Select
                placeholder="Filter by Verification"
                value={filters.verification_status}
                onChange={(value) =>
                  handleFilterChange("verification_status", value)
                }
                style={{ width: "100%" }}
              >
                <Option value="all">All Verification</Option>
                <Option value="verified">Verified</Option>
                <Option value="unverified">Unverified</Option>
              </Select>
            </Col>

            <Col xs={24} sm={12} md={8}>
              <div className="flex items-center gap-2">
                <Button
                  icon={<RefreshCw className="h-4 w-4" />}
                  onClick={fetchUsers}
                  loading={usersResponse?.isLoading}
                >
                  Refresh
                </Button>

                <span className="text-sm text-gray-500">
                  {pagination.count || 0} users
                </span>
              </div>
            </Col>
          </Row>
        </Card>

        {/* Summary Cards */}
        <Row gutter={16} className="mb-4">
          <Col xs={12} sm={6}>
            <Card size="small">
              <div className="text-center">
                <div className="text-xl font-bold text-blue-600">
                  {pagination.count || 0}
                </div>
                <div className="text-sm text-gray-600">Total Users</div>
              </div>
            </Card>
          </Col>

          <Col xs={12} sm={6}>
            <Card size="small">
              <div className="text-center">
                <div className="text-xl font-bold text-green-600">
                  {users.filter((u) => u.is_verified).length}
                </div>
                <div className="text-sm text-gray-600">Verified</div>
              </div>
            </Card>
          </Col>

          <Col xs={12} sm={6}>
            <Card size="small">
              <div className="text-center">
                <div className="text-xl font-bold text-orange-600">
                  {users.filter((u) => !u.is_verified).length}
                </div>
                <div className="text-sm text-gray-600">Unverified</div>
              </div>
            </Card>
          </Col>

          <Col xs={12} sm={6}>
            <Card size="small">
              <div className="text-center">
                <div className="text-xl font-bold text-purple-600">
                  {users.filter((u) => u.total_orders > 0).length}
                </div>
                <div className="text-sm text-gray-600">With Orders</div>
              </div>
            </Card>
          </Col>
        </Row>

        {/* Users Table */}
        <Table
          columns={columns}
          dataSource={users}
          loading={usersResponse?.isLoading}
          rowKey="id"
          pagination={{
            current: filters.pageNumber,
            total: pagination.count || 0,
            pageSize: 10,
            showSizeChanger: false,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} users`,
            onChange: handlePageChange,
          }}
          scroll={{ x: 800 }}
          size="small"
        />
      </div>
    </>
  );
}

export default UsersTable;
